/**
 * JWT Token Storage
 * 
 * Manages JWT token storage and retrieval from localStorage.
 */

const USER_DATA_KEY = 'pivot_user_data';

let inMemoryToken: string | null = null;

export interface UserData {
  email?: string;
  displayName?: string;
  userId?: string;
}

/**
 * Get JWT token from in-memory storage
 */
export function getJWTToken(): string | null {
  return inMemoryToken;
}

/**
 * Save JWT token to in-memory storage
 */
export function saveJWTToken(token: string): void {
  inMemoryToken = token;
}

/**
 * Remove JWT token from memory
 */
export function removeJWTToken(): void {
  inMemoryToken = null;
}

/**
 * Get user data from localStorage
 */
export function getUserData(): UserData | null {
  try {
    const data = localStorage.getItem(USER_DATA_KEY);
    if (data) {
      return JSON.parse(data);
    }
    return null;
  } catch (error) {
    console.error('[TokenStorage] Error getting user data:', error);
    return null;
  }
}

/**
 * Save user data to localStorage
 */
export function saveUserData(userData: UserData): void {
  try {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  } catch (error) {
    console.error('[TokenStorage] Error saving user data:', error);
  }
}

/**
 * Remove user data from localStorage
 */
export function removeUserData(): void {
  try {
    localStorage.removeItem(USER_DATA_KEY);
  } catch (error) {
    console.error('[TokenStorage] Error removing user data:', error);
  }
}

/**
 * Decode JWT token (without verification - client-side only)
 * Returns payload or null if invalid
 */
function decodeJWT(token: string): { exp?: number; [key: string]: unknown } | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode payload (base64url)
    const payload = parts[1];
    // Replace URL-safe base64 characters
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('[TokenStorage] Error decoding JWT:', error);
    return null;
  }
}

/**
 * Check if user is authenticated (has valid, non-expired JWT token)
 */
export function isAuthenticated(): boolean {
  const token = getJWTToken();
  if (!token || token.length === 0) return false;
  
  // Decode JWT to check expiration
  const payload = decodeJWT(token);
  if (!payload) return false;
  
  // Check expiration (exp is Unix timestamp in seconds)
  if (payload.exp) {
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    if (now >= expirationTime) {
      // Token expired, clear it
      clearAuthData();
      return false;
    }
  }
  
  return true;
}

/**
 * Clear all auth data
 */
export function clearAuthData(): void {
  removeJWTToken();
  removeUserData();
}

/**
 * Refresh JWT token using refresh token from cookie
 */
export async function refreshToken(): Promise<string | null> {
  try {
    const refreshUrl = import.meta.env.DEV
      ? 'http://localhost:8789/refresh'
      : 'https://pivot-is-auth-api.dont-d23.workers.dev/refresh';
    
    const response = await fetch(refreshUrl, {
      method: 'POST',
      credentials: 'include', // Include cookies
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (data.success && data.accessToken) {
      saveJWTToken(data.accessToken);
      return data.accessToken;
    }
    return null;
  } catch (error) {
    console.error('[TokenStorage] Token refresh failed:', error);
    return null;
  }
}

/**
 * Get token, automatically refresh if expired or about to expire
 */
export async function getValidToken(): Promise<string | null> {
  let token = getJWTToken();
  if (!token) {
    token = await refreshToken();
    if (!token) {
      return null;
    }
  }

  const payload = decodeJWT(token);
  if (!payload) return null;

  // Check if token expires in less than 5 minutes
  if (payload.exp) {
    const expirationTime = payload.exp * 1000;
    const now = Date.now();
    const timeUntilExpiry = expirationTime - now;
    
    // If expired or expires in less than 5 minutes, try to refresh
    if (timeUntilExpiry < 5 * 60 * 1000) {
      const newToken = await refreshToken();
      if (newToken) {
        return newToken;
      }
      // If refresh failed and token is expired, clear auth
      if (timeUntilExpiry <= 0) {
        clearAuthData();
        return null;
      }
    }
  }

  return token;
}

